import React, {useState, useRef, useMemo, PropsWithChildren} from 'react';
import cx from 'classnames';
import {Formik,  useFormikContext, FormikValues} from "formik";
import axios, {AxiosError, AxiosRequestConfig, AxiosResponse} from 'axios'
import { Button, Alert, StoreOnBlur } from "fearless-common-ui";
import {FormikConfig} from "formik/dist/types";
import Loading from "../Loading";
import {beautifyErrorMessage} from "../../utils/error";
import usePreference from "../hooks/usePreference";
import useBackendFormContext, {BackendFormContextProvider} from "./BackendFormContext";
import {SuccessResponseFormat} from "fearless-common/src/backendResponses";

const { post, get, put } = axios


export default function BackendForm<Values extends FormikValues, SuccessData = {}>({
    initialValues: propsInitialValues,
    children,
    targetEndpoint,
    targetMethod = 'GET',
    config = undefined,
    onSuccess = undefined,
    enableReinitialize = false,
    footerContent,
    showFooter = true,
    onError = undefined,
    validationSchema,
    formId,
    rememberForm = false,
}: PropsWithChildren<{
    initialValues: FormikConfig<Values>['initialValues'];
    onSuccess?: (resp: SuccessResponseFormat<SuccessData>['data']) => void;
    onError?: ({ status }: { status: number }) => void;
    enableReinitialize?: boolean;
    config?: AxiosRequestConfig;
    showFooter?: boolean;
    targetMethod: 'POST' | 'GET' | 'PUT' | 'DELETE';
    targetEndpoint: string;
    formId?: string;
    footerContent?: React.JSX.Element;
    validationSchema?: FormikConfig<Values>['validationSchema'];
    /**
     * Store the filled in UNFINISHED form until the next time the user visits the form.
     * After finishing the form, then memory is cleared.
     * */
    rememberForm?: boolean
}>) {
    const timeoutRef = useRef<NodeJS.Timeout>(null);
    const [error, setError] = useState('');
    const [feedback, setFeedback] = useState('');
    const preferenceObj = useMemo(() => ({
        key: `remember-${formId}`,
        // Only store form information for 6 hours = 0.25 days for security purposes.
        // Forms can contain sensitive information such as passwords and email
        // addresses.
        reminderDays: 0.25,
    }), [formId]);
    const { preferenceValue: storedInitialValues, setPreferenceValue: storeInitialValues } = usePreference<Values>(
        // Disabled storing and saving preference if rememberForm=false
        rememberForm ? preferenceObj : undefined
    );

    const initialValues = useMemo(() => {
        if (rememberForm && typeof storedInitialValues === 'object' && storedInitialValues) {
            return Object.keys(storedInitialValues).reduce((acc, key) => {
                // Only use the keys from the saved
                if (key in storedInitialValues && storedInitialValues[key]) {
                    // @ts-ignore
                  acc[key] = storedInitialValues[key];
                }
                return acc;
            }, {...propsInitialValues});
        } else {
            return propsInitialValues;
        }
    }, [storedInitialValues, propsInitialValues, rememberForm]);

    const saveValuesToRemember = rememberForm ? storeInitialValues : undefined;

    return (
        <BackendFormContextProvider value={{ error, feedback, footerContent }}>
            <Formik<Values>
                enableReinitialize={enableReinitialize}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, actions) => {
                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                    setFeedback('')
                    setError('')

                    if (saveValuesToRemember) {
                        // Store the values in a persistent storage
                        saveValuesToRemember(values);
                    }

                    const thenCallback = (response: AxiosResponse<SuccessData>) => {
                      // @ts-ignore
                      if (onSuccess) onSuccess(response.data as SuccessData)
                      // @ts-ignore
                      const { message } = response.data;
                      if (message) setFeedback(message)

                      if (rememberForm) {
                        // Clear the stored form details after a succesful submit
                        storeInitialValues(undefined);
                      }

                      // Remove the feedback after some time.
                      timeoutRef.current = setTimeout(() => {
                        setFeedback('')
                      }, 3000)
                    };
                    const catchCallback = (e: AxiosError) => {
                      const data = e.response?.data;
                      if (data) {
                        // @ts-ignore
                        const { message, errors } = data;

                        setError(message)
                        for (const errorsKey in errors) {
                          const value = errors[errorsKey]
                          actions.setFieldError(
                            errorsKey,
                            Array.isArray(value) ? value?.join(' ') : value,
                          )
                        }
                        if (onError && e.response) onError(e.response)
                      } else {
                        setError(beautifyErrorMessage(e))
                      }
                    };
                    const finallyCallback = () => {
                      actions.setSubmitting(false)
                    };
                    if (targetMethod === 'POST') {
                        post<SuccessData>(targetEndpoint, { ...values, token_name: 'web-app' }, config)
                          .then(thenCallback)
                          .catch(catchCallback)
                          .finally(finallyCallback)
                    } else if (targetMethod === 'PUT') {
                      put<SuccessData>(targetEndpoint, { ...values, token_name: 'web-app' }, config)
                        .then(thenCallback)
                        .catch(catchCallback)
                        .finally(finallyCallback)
                    } else {
                      get<SuccessData>(targetEndpoint, { ...values, token_name: 'web-app' })
                        .then(thenCallback)
                        .catch(catchCallback)
                        .finally(finallyCallback)
                    }
                }}
            >
                {({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} action="post" id={formId}>
                        {children}
                        {showFooter && <BackendForm.Footer />}
                        {saveValuesToRemember && <StoreOnBlur saveValuesToRemember={saveValuesToRemember}/>}
                    </form>
                )}
            </Formik>
        </BackendFormContextProvider>
    );
}

function BackendFormFooter({
                             showError = true,
                             marginTop = true,
                             buttonText = 'Submit',
                             className = '',
                             center = false,
                             buttonVariant = 'primary',
                           }: {
  showError?: boolean;
  marginTop?: boolean;
  center?: boolean;
  className?: string;
  buttonText?: string | React.JSX.Element;
  buttonVariant?: 'primary' | 'danger';
}) {
    const { footerContent } = useBackendFormContext();
    const { isSubmitting } = useFormikContext();
    return (
        <>
            <BackendFormErrorFeedback showError={showError} />
            <div className={cx({
                'd-flex justify-content-end' : !center,
                'd-flex justify-content-center' : center,
                'mt-5' : marginTop,
            })}>
                {footerContent && (
                    <div className="me-2 flex-grow-1">{footerContent}</div>
                )}
                <Button
                    className={cx({
                        "ps-5": true,
                        "pe-5": !isSubmitting,
                        "pe-3": isSubmitting,
                        [className]: !!className,
                    })}
                    size="lg"
                    variant={buttonVariant}
                    type="submit"
                    disabled={isSubmitting}
                >
                    {buttonText}
                    {isSubmitting && <Loading />}
                </Button>
            </div>
        </>
    )
}

/**
 * Show the submit error/feedback from the form.
 * With this component you can show it at multiple spots.
 */
function BackendFormErrorFeedback({ showError }: { showError?: boolean }) {
    const { error, feedback } = useBackendFormContext();

    return <>
        {showError && error ? <Alert variant="danger">{error}</Alert> : null}
        {feedback && <Alert variant="success">{feedback}</Alert>}
    </>;
}
BackendForm.Footer = BackendFormFooter;
BackendForm.Feedback = BackendFormErrorFeedback;
